import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Pencil, Check, X, Wand2, Loader2 } from "lucide-react";

const BUILTINS = [
  { value: "", label: "— Unassigned —" },
  { value: "admin", label: "Admin (built-in)" },
  { value: "qa", label: "QA (built-in)" },
  { value: "agent", label: "Agent (built-in)" },
];

export default function AgentsTab({ agents, roles, onCreate, onUpdate, onDelete, onRefresh, onRolesRefresh }) {
  const [form, setForm] = useState({ name: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", role: "" });
  const [migrating, setMigrating] = useState(false);

  const roleOptions = useMemo(() => {
    const custom = (roles || []).map((r) => ({ value: r.name, label: `${r.name} (custom · ${r.base_role})` }));
    return [...BUILTINS, ...custom];
  }, [roles]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await onCreate({ name: form.name.trim(), role: form.role });
      setForm({ name: "", role: "" });
    } finally { setBusy(false); }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditDraft({ name: a.name || "", role: a.role || "" });
  };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (id) => {
    if (!editDraft.name.trim()) return toast.error("Name is required");
    await onUpdate(id, { name: editDraft.name.trim(), role: editDraft.role });
    setEditingId(null);
  };

  const hasLegacyEmailLabels = useMemo(() => {
    return (agents || []).some((a) => !a.role && a.email && !a.email.includes("@"));
  }, [agents]);

  const migrateLegacy = async () => {
    if (!window.confirm("This will convert each agent's 'Email' value (when it's not an email) into a proper role and create matching custom roles where needed. Continue?")) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/agents/migrate-email-to-role");
      toast.success(`Migrated ${data.agents_updated} agent${data.agents_updated === 1 ? "" : "s"}. Created ${data.roles_created.length} new role${data.roles_created.length === 1 ? "" : "s"}.`);
      await onRefresh();
      await onRolesRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="agents-tab">
      <form onSubmit={submit} className="flex flex-wrap gap-2 items-end" data-testid="agents-create-form">
        <div className="flex-1 min-w-[200px]">
          <label className="meta-label">Name</label>
          <Input
            data-testid="agents-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Aisha Khan"
            className="rounded-sm h-10 mt-1"
            required
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="meta-label">Role</label>
          <select
            data-testid="agents-role-input"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full mt-1 px-3 h-10 border border-input bg-background text-sm rounded-sm"
          >
            {roleOptions.map((o) => <option key={o.value || "__empty"} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <Button type="submit" data-testid="agents-create-button" disabled={busy} className="rounded-sm bg-primary hover:bg-[#00227A] h-10">
          <Plus size={14} className="mr-1" /> Add
        </Button>
        {hasLegacyEmailLabels && (
          <Button
            type="button"
            data-testid="agents-migrate-email-button"
            onClick={migrateLegacy}
            disabled={migrating}
            variant="outline"
            className="rounded-sm h-10"
            title="Convert each non-email value in the legacy Email column into a proper role"
          >
            {migrating ? <Loader2 className="animate-spin mr-1" size={14} /> : <Wand2 size={14} className="mr-1" />}
            Migrate Email → Role
          </Button>
        )}
      </form>

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left border-b border-border">
              <th className="px-4 py-2 meta-label">Name</th>
              <th className="px-4 py-2 meta-label">Role</th>
              <th className="px-4 py-2 meta-label">Email <span className="opacity-60 text-[10px] normal-case">(optional)</span></th>
              <th className="px-4 py-2 meta-label">Created</th>
              <th className="px-4 py-2 meta-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(!agents || agents.length === 0) && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No agents yet.</td></tr>
            )}
            {(agents || []).map((a) => {
              const isEdit = editingId === a.id;
              const isLegacy = !a.role && a.email && !a.email.includes("@");
              const displayRole = a.role || "";
              const meta = roles.find((r) => r.name === displayRole);
              const tone = meta
                ? (meta.base_role === "admin" ? "badge-info" : meta.base_role === "qa" ? "badge-warning" : "badge-neutral")
                : (displayRole === "admin" ? "badge-info" : displayRole === "qa" ? "badge-warning" : displayRole === "agent" ? "badge-neutral" : "");
              return (
                <tr key={a.id} className="border-b border-border last:border-0" data-testid={`agent-row-${a.id}`}>
                  <td className="px-4 py-2 font-medium align-middle">
                    {isEdit ? (
                      <Input
                        autoFocus
                        data-testid={`agents-edit-name-${a.id}`}
                        value={editDraft.name}
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        className="rounded-sm h-9"
                      />
                    ) : a.name}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    {isEdit ? (
                      <select
                        data-testid={`agents-edit-role-${a.id}`}
                        value={editDraft.role}
                        onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })}
                        className="px-2 h-9 border border-input bg-background text-sm rounded-sm"
                      >
                        {roleOptions.map((o) => <option key={o.value || "__empty"} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : displayRole ? (
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm ${tone}`}>
                        {displayRole.toUpperCase()}{meta ? <span className="opacity-70 ml-1">· {meta.base_role}</span> : null}
                      </span>
                    ) : isLegacy ? (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm badge-warning" title="Legacy label still stored in Email column">
                        {a.email} <span className="opacity-60">· legacy</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs align-middle text-muted-foreground">
                    {isEdit
                      ? <span className="text-muted-foreground text-xs">—</span>
                      : (a.email && a.email.includes("@") ? a.email : "—")}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground align-middle">{(a.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2 text-right align-middle">
                    {isEdit ? (
                      <div className="inline-flex items-center gap-2">
                        <button data-testid={`agents-save-${a.id}`} onClick={() => saveEdit(a.id)} className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1">
                          <Check size={12} /> Save
                        </button>
                        <button data-testid={`agents-cancel-${a.id}`} onClick={cancelEdit} className="text-muted-foreground hover:underline text-xs inline-flex items-center gap-1">
                          <X size={12} /> Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-3">
                        <button data-testid={`agents-edit-${a.id}`} onClick={() => startEdit(a)} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                          <Pencil size={12} /> Edit
                        </button>
                        <button data-testid={`delete-agents-${a.id}`} onClick={() => onDelete(a.id)} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1">
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
